import React, { useEffect, useRef, useCallback } from 'react';
import { FolderPlus, FileCode, FileText, Pencil, Trash2 } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface FileTreeContextMenuProps {
  x: number;
  y: number;
  nodeId: string | null; // null means clicked on empty area
  nodeType: 'file' | 'folder' | null;
  onClose: () => void;
  onCreateFolder: (parentId: string | null) => void | Promise<void>;
  onCreateScript: (parentId: string | null, language: 'typescript' | 'javascript') => void | Promise<void>;
  onRename: (nodeId: string) => void | Promise<void>;
  onDelete: (nodeId: string) => void | Promise<void>;
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, onClick, variant = 'default' }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 w-full px-3 py-1.5 text-sm transition-colors',
      variant === 'danger'
        ? 'text-red-400 hover:bg-zinc-700 hover:text-red-300'
        : 'text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100'
    )}
  >
    {icon}
    <span>{label}</span>
  </button>
);

const MenuSeparator: React.FC = () => (
  <div className="my-1 border-t border-zinc-700" />
);

export const FileTreeContextMenu: React.FC<FileTreeContextMenuProps> = ({
  x,
  y,
  nodeId,
  nodeType,
  onClose,
  onCreateFolder,
  onCreateScript,
  onRename,
  onDelete,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Adjust position if menu would go off screen
  const getAdjustedPosition = useCallback(() => {
    const menuWidth = 200;
    const menuHeight = nodeType === 'file' ? 80 : nodeId ? 180 : 120;

    let adjustedX = x;
    let adjustedY = y;

    // Adjust for right edge
    if (x + menuWidth > window.innerWidth) {
      adjustedX = window.innerWidth - menuWidth - 8;
    }

    // Adjust for bottom edge
    if (y + menuHeight > window.innerHeight) {
      adjustedY = window.innerHeight - menuHeight - 8;
    }

    // Ensure minimum bounds
    adjustedX = Math.max(8, adjustedX);
    adjustedY = Math.max(8, adjustedY);

    return { x: adjustedX, y: adjustedY };
  }, [x, y, nodeId, nodeType]);

  const position = getAdjustedPosition();

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add listeners with capture phase to catch all clicks
    // Use both mousedown and click for better coverage
    const addListeners = () => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
    };

    // Add listener with a small delay to prevent immediate close from the triggering right-click
    const timeoutId = setTimeout(addListeners, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [onClose]);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleCreateFolder = () => {
    onCreateFolder(nodeType === 'folder' ? nodeId : null);
    onClose();
  };

  const handleCreateTypeScript = () => {
    onCreateScript(nodeType === 'folder' ? nodeId : null, 'typescript');
    onClose();
  };

  const handleCreateJavaScript = () => {
    onCreateScript(nodeType === 'folder' ? nodeId : null, 'javascript');
    onClose();
  };

  const handleRename = () => {
    if (nodeId) {
      onRename(nodeId);
      onClose();
    }
  };

  const handleDelete = () => {
    if (nodeId) {
      onDelete(nodeId);
      onClose();
    }
  };

  // For files, show only rename and delete
  if (nodeType === 'file') {
    return (
      <div
        ref={menuRef}
        className="fixed z-50 min-w-[180px] py-1 bg-zinc-800 border border-zinc-700 rounded-md shadow-lg shadow-black/50"
        style={{ left: position.x, top: position.y }}
      >
        <MenuItem
          icon={<Pencil className="w-4 h-4" />}
          label="Rename"
          onClick={handleRename}
        />
        <MenuItem
          icon={<Trash2 className="w-4 h-4" />}
          label="Delete"
          onClick={handleDelete}
          variant="danger"
        />
      </div>
    );
  }

  // For folders or empty area, show create options
  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[180px] py-1 bg-zinc-800 border border-zinc-700 rounded-md shadow-lg shadow-black/50"
      style={{ left: position.x, top: position.y }}
    >
      <MenuItem
        icon={<FolderPlus className="w-4 h-4" />}
        label="New Folder"
        onClick={handleCreateFolder}
      />
      <MenuItem
        icon={<FileCode className="w-4 h-4 text-blue-400" />}
        label="New TypeScript File"
        onClick={handleCreateTypeScript}
      />
      <MenuItem
        icon={<FileText className="w-4 h-4 text-yellow-400" />}
        label="New JavaScript File"
        onClick={handleCreateJavaScript}
      />
      {nodeId && (
        <>
          <MenuSeparator />
          <MenuItem
            icon={<Pencil className="w-4 h-4" />}
            label="Rename"
            onClick={handleRename}
          />
          <MenuItem
            icon={<Trash2 className="w-4 h-4" />}
            label="Delete"
            onClick={handleDelete}
            variant="danger"
          />
        </>
      )}
    </div>
  );
};
